import {assertIsFunction, assertIsObject} from './types-utils';
import {isEqual} from 'lodash-es';


/**
 * In-house implementation of `omitBy` from lodash.
 * Removes properties from an object based on a predicate function.
 * The function iterates over the object's entries and excludes properties for which the predicate returns true.
 *
 * @template T
 * @param {T} object - The object from which properties are to be omitted. Must be a valid plain object.
 * @param {(value: T[keyof T], key?: keyof T) => boolean} predicate - A function invoked per property.
 * Receives the property value and key as arguments.
 * Returns true to omit the property or false to retain it.
 * @return {Partial<T>} A new object with the properties omitted based on the predicate function.
 */
export function omitPropertiesBy<T extends Record<string, any>>(object: T, predicate: (value: T[keyof T], key?: keyof T) => boolean): Partial<T> {
    assertIsObject(object);
    assertIsFunction(predicate);
    return Object.fromEntries(Object.entries(object).filter(([key, value]) => !predicate(value, key))) as Partial<T>;
}

/**
 * In-house implementation of `pickBy` from lodash.
 * Filters the properties of an object based on a predicate function and returns a new object containing only the properties that satisfy the predicate.
 *
 * @template T
 * @param {T} object - The source object from which properties should be selected.
 * @param {(value: T[keyof T], key?: keyof T) => boolean} predicate - A function that determines which properties to include based on their value and key. It should return `true` for properties to include and `false` for properties to exclude.
 * @return {Partial<T>} A new object containing only the properties of the input object that satisfy the predicate function.
 */
export function pickPropertiesBy<T extends Record<string, any>>(object: T, predicate: (value: T[keyof T], key?: keyof T) => boolean): Partial<T> {
    assertIsObject(object);
    assertIsFunction(predicate);
    return Object.fromEntries(Object.entries(object).filter(([key, value]) => predicate(value, key))) as Partial<T>;
}

/**
 * In-house implementation of `pick` from lodash.
 * Selects and returns a new object containing only the properties of the input object
 * that match the specified keys.
 *
 * @template T
 * @param {T} object - The source object from which properties are selected.
 * @param {(keyof T)[]} keys - An array of keys specifying which properties to pick from the source object.
 * @return {Partial<T>} A new object containing only the properties from the input object that match the specified keys.
 */
export function pickPropertiesByKeys<T extends Record<string, any>>(object: T, keys: (keyof T)[]): Partial<T> {
    return pickPropertiesBy(object, (value, key) => keys.includes(key));
}

/**
 * @module custom-utils
 * @name deepFreeze
 * @kind function
 *
 * @template T
 *
 * @param {T} object Object to freeze
 * @returns {T} Frozen object
 *
 * @description
 * Performs deep freeze on given object
 */
export function deepFreeze<T extends Record<string, any>>(object: T): T {
    Object.freeze(object);

    Object.getOwnPropertyNames(object).forEach((prop) => {
        if (Object.hasOwn(object, prop)
            && object[prop] !== null
            && (typeof object[prop] === 'object' || typeof object[prop] === 'function')
            && !Object.isFrozen(object[prop])) {

            deepFreeze(object[prop]);
        }
    });

    return object;
}

/**
 * @module custom-utils
 * @name isEmptyObject
 * @kind function
 *
 * @param {object} value
 *
 * @returns {boolean} true if is empty
 *
 * @description
 * Checks if the object is equal to {} (has no properties)
 */
export function isEmptyObject(value: Record<string, any>): boolean {
    return Object.keys(value).length === 0 // no props
        && value.constructor === Object; // for primitive values wrapper classes (new Number())
}

/**
 * Compares two dictionary objects and returns the differences as a new object.
 *
 * @template T
 * @param {Partial<T> | null} obj1 - The first dictionary object to compare. If null, it is treated as an empty object.
 * @param {Partial<T> | null} obj2 - The second dictionary object to compare. If null, it is treated as an empty object.
 * @return {Partial<T>} A new object containing the properties from obj2 that are different from obj1.
 */
export function diffDictionaryObjects<T extends Record<string, any>>(obj1: Partial<T> | null, obj2: Partial<T> | null): Partial<T> {
    const diff: Partial<T> = {};
    const normalisedObj1: Partial<T> = obj1 ?? {};
    const normalisedObj2: Partial<T> = obj2 ?? {};
    const keys: Set<keyof T> = new Set([...Object.keys(normalisedObj1), ...Object.keys(normalisedObj2)]);

    keys.forEach((key) => {
        if (!isEqual(normalisedObj1[key], normalisedObj2[key])) {
            diff[key] = normalisedObj2[key];
        }
    });
    return diff;
}